import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AuditCadence, AuditListData, AuditSummary } from '../../api/types.js';
import { loadPersisted, savePersisted } from '../../state/persist.js';
import { color, font, fontSize, radius, space } from '../../theme/tokens.js';
import { formatNumber } from '../MetricCell/format.js';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl.js';
import { EmptyState } from '../States/States.js';
import { CadencePill, formatRunDate } from './shared.js';

/**
 * State 1 — the audit list (blueprint lines 972–1009, PRD-Audit §6.3).
 *
 * The headline under each audit is where the honesty rules show:
 *
 * - a **reporting** audit (never flagged by the service) shows its focus total in neutral ink and
 *   never `Clean` — Aged Candidates would otherwise read clean on the night 519 candidates reached
 *   their unassignment deadline (AU-05);
 * - an **uncatalogued** audit shows `N flagged` or its focus total, never `Clean` (AU-17);
 * - an audit with **no runs** says so rather than drawing zeros (FR-AU12).
 */

type CadenceFilter = 'all' | AuditCadence;
const CADENCE_KEY = 'audit.cadence';

function sanitizeCadence(raw: unknown): CadenceFilter | null {
  return raw === 'all' || raw === 'Daily' || raw === 'Weekly' || raw === 'Monthly' ? raw : null;
}

/** `519 at deadline` — the focus figure named by its label; a label starting with a digit is set apart. */
export function focusHeadline(audit: AuditSummary): string {
  const total = formatNumber(audit.latestRun?.focusTotal ?? 0);
  const label = audit.labels.focus.short;
  return /^\d/.test(label) ? `${total} · ${label}` : `${total} ${label.toLowerCase()}`;
}

export function headlineOf(audit: AuditSummary): { text: string; ink: string } | null {
  const run = audit.latestRun;
  if (!run) return null;
  if (audit.kind === 'reporting') return { text: focusHeadline(audit), ink: color.inkMuted };
  if (audit.kind === 'uncatalogued') {
    return run.flagged
      ? { text: `${formatNumber(run.flagged)} flagged`, ink: color.negative }
      : { text: focusHeadline(audit), ink: color.inkMuted };
  }
  return run.flagged
    ? { text: `${formatNumber(run.flagged)} flagged`, ink: color.negative }
    : { text: 'Clean', ink: color.inkMintStrong };
}

function AuditRow({ audit }: { audit: AuditSummary }) {
  const run = audit.latestRun;
  const headline = headlineOf(audit);
  const lagging = run?.laggingMissed ?? null;

  return (
    <Link
      to={`/audit/${encodeURIComponent(audit.id)}`}
      className="trow"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 18px',
        borderTop: `1px solid ${color.borderSubtle}`,
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <CadencePill cadence={audit.cadence} note={audit.cadenceNote} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: fontSize.bodyStrong, fontWeight: 600, color: color.ink }}>{audit.name}</div>
        <div style={{ fontSize: fontSize.caption, color: color.inkFaint, marginTop: 2 }}>
          {run ? run.summary : 'No runs yet.'}
        </div>
      </div>
      <div style={{ textAlign: 'right', flex: 'none' }}>
        {headline ? (
          <div
            className="mono"
            style={{ fontFamily: font.mono, fontSize: fontSize.captionStrong, fontWeight: 600, color: headline.ink }}
          >
            {headline.text}
          </div>
        ) : null}
        <div
          style={{ fontSize: fontSize.label, color: lagging ? color.warning : color.inkFaint }}
          title={lagging ? `This audit did not run on ${formatRunDate(lagging)}` : undefined}
        >
          {run ? `Last run ${formatRunDate(run.runDate)}` : 'Not yet run by the audit service'}
        </div>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color.inkHint} strokeWidth="2.4" aria-hidden="true" style={{ flex: 'none' }}>
        <path d="m9 18 6-6-6-6" />
      </svg>
    </Link>
  );
}

export function AuditList({ data }: { data: AuditListData }) {
  const [cadence, setCadence] = useState<CadenceFilter>(
    () => loadPersisted(CADENCE_KEY, sanitizeCadence) ?? 'all',
  );
  const choose = (next: CadenceFilter) => {
    setCadence(next);
    savePersisted(CADENCE_KEY, next);
  };

  const sections = data.sections
    .map((s) => ({
      ...s,
      // The cadence is the service's (AU-20). An audit with none — a frequency it did not send or
      // that this page cannot read — makes no cadence claim, so only "All" shows it.
      audits: s.audits.filter((a) => cadence === 'all' || a.cadence === cadence),
    }))
    .filter((s) => s.audits.length > 0);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: fontSize.body, color: color.inkMuted, maxWidth: 640, lineHeight: 1.5 }}>
          Automated checks that run nightly, weekly, or monthly against the book of business. Click
          an audit to see its run history and results.
        </div>
        <SegmentedControl
          options={[
            { value: 'all' as CadenceFilter, label: 'All' },
            { value: 'Daily' as CadenceFilter, label: 'Daily' },
            { value: 'Weekly' as CadenceFilter, label: 'Weekly' },
            { value: 'Monthly' as CadenceFilter, label: 'Monthly' },
          ]}
          value={cadence}
          onChange={choose}
          label="Cadence"
        />
      </div>

      {sections.length === 0 ? (
        <EmptyState title="No audits at this frequency." />
      ) : (
        sections.map((section) => (
          <section key={section.name} style={{ marginBottom: 22 }} aria-label={section.name}>
            <h2
              style={{
                margin: '0 0 10px',
                fontSize: fontSize.caption,
                fontWeight: 700,
                letterSpacing: '.4px',
                color: color.inkMuted,
                textTransform: 'uppercase',
              }}
            >
              {section.name}
            </h2>
            <div
              style={{
                background: color.surface,
                border: `1px solid ${color.border}`,
                borderRadius: radius['6xl'],
                overflow: 'hidden',
              }}
            >
              {section.audits.map((audit) => (
                <AuditRow key={audit.id} audit={audit} />
              ))}
            </div>
          </section>
        ))
      )}
      <div style={{ height: space.xs }} />
    </div>
  );
}
